<template>
  <el-dialog
    :title="title"
    :model-value="visible"
    width="70%"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <div class="department-user-selector">
      <!-- 顶部汇总栏 - 按旗县分组 -->
      <div class="summary-bar">
        <span class="title">已选人员：</span>
        <template v-if="selectedUsers.length > 0">
          <div class="grouped-summary">
            <div 
              v-for="group in groupedSelectedUsers" 
              :key="group.departmentId"
              class="county-group"
            >
              <div class="county-label">{{ group.departmentName }}</div>
              <div class="county-users">
                <el-tag
                  v-for="user in group.users"
                  :key="user.userId"
                  :closable="mode === 'multiple'"
                  @close="removeSelection(user.userId)"
                  size="small"
                  class="user-tag"
                >
                  {{ user.userName }}
                </el-tag>
              </div>
            </div>
          </div>
        </template>
        <span v-else class="empty-tip">暂未选择人员</span>
        <el-button
          v-if="selectedUsers.length > 0 && mode === 'multiple'"
          type="text"
          @click="clearAll"
          class="clear-btn"
        >
          全清
        </el-button>
      </div>

      <div class="selector-container">
        <!-- 左侧旗县树 -->
        <div class="department-tree">
          <el-input
            v-model="searchText"
            placeholder="搜索旗县/人员"
            clearable
          />
          <el-tree
            ref="treeRef"
            :data="processedDepartments"
            node-key="id"
            :props="treeProps"
            :filter-node-method="filterNode"
            :highlight-current="true"
            :default-expand-all="false"
            @node-click="handleNodeClick"
          >
            <template #default="{ node, data }">
              <span class="tree-node">
                <span v-if="data.isDepartment">
                  {{ node.label }}
                  <el-icon v-if="isDepartmentSelected(data.id)">
                    <Check />
                  </el-icon>
                </span>
                <span v-else>
                  <el-icon>
                    <User />
                  </el-icon>
                  <span class="user-name">{{ data.name }}</span>
                </span>
              </span>
            </template>
          </el-tree>
        </div>

        <!-- 右侧人员卡片 -->
        <div class="user-cards">
          <template v-if="currentDepartment">
            <h3 class="department-title">
              {{ currentDepartment.name }}
              <el-icon v-if="isDepartmentSelected(currentDepartment.id)">
                <Check />
              </el-icon>
            </h3>
            <div class="card-container">
              <template
                v-if="
                  currentDepartment.users && currentDepartment.users.length > 0
                "
              >
                <div
                  v-for="user in currentDepartment.users"
                  :key="user.id"
                  class="user-card"
                  :class="{ selected: isUserSelected(user.id) }"
                  @click="selectUser(user)"
                >
                  <div class="user-icon">
                    <el-icon><User /></el-icon>
                  </div>
                  <div class="user-info">
                    <div class="name">{{ user.name }}</div>
                    <div class="position">{{ user.position }}</div>
                  </div>
                  <div v-if="isUserSelected(user.id)" class="selected-marker">
                    <el-icon><Check /></el-icon>
                  </div>
                </div>
              </template>
              <div v-else class="empty-tip">该旗县暂无人员</div>
            </div>
          </template>
          <div v-else class="empty-tip">请选择旗县</div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleCancel">取 消</el-button>
        <el-button type="primary" @click="handleConfirm">确 定</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import { Check, User } from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";

export default {
  name: "DepartmentUserSelector",
  components: { Check, User },
  props: {
    // 旗县数据，格式：[{id, name, users: [{id, name, position, avatar}]}]
    departments: {
      type: Array,
      required: true,
    },
    // 对话框标题
    title: {
      type: String,
      default: "选择旗县人员",
    },
    // 控制对话框显示
    visible: {
      type: Boolean,
      default: false,
    },
    // 选择模式：single-单选，multiple-多选
    mode: {
      type: String,
      default: "single",
      validator: (value) => ["single", "multiple"].includes(value),
    },
    // 是否限制每个旗县只能选择一人
    limitOnePerDepartment: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:visible", "confirm"],
  data() {
    return {
      searchText: "",
      currentDepartment: null,
      selectedUsers: [], // 格式: [{departmentId, departmentName, userId, userName}]
      treeProps: {
        label: "name",
        children: "children",
      },
    };
  },
  computed: {
    // 处理旗县数据为树形结构
    processedDepartments() {
      return this.departments.map((dept) => ({
        id: `dept_${dept.id}`,
        name: dept.name,
        isDepartment: true,
        children: dept.users.map((user) => ({
          id: `user_${user.id}`,
          name: user.name,
          avatar: user.avatar,
          position: user.position,
          departmentId: dept.id,
          isDepartment: false,
        })),
      }));
    },
    // 按旗县分组已选用户
    groupedSelectedUsers() {
      const groups = {};
      this.selectedUsers.forEach(user => {
        if (!groups[user.departmentId]) {
          groups[user.departmentId] = {
            departmentId: user.departmentId,
            departmentName: user.departmentName,
            users: []
          };
        }
        groups[user.departmentId].users.push(user);
      });
      return Object.values(groups);
    },
  },
  watch: {
    searchText(val) {
      this.$refs.treeRef.filter(val);
    },
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.expandFirstDepartment();
        });
      }
    },
  },
  created() {
    if (this.visible) {
      this.$nextTick(() => {
        this.expandFirstDepartment();
      });
    }
  },
  methods: {
    // 展开第一个旗县
    expandFirstDepartment() {
      if (this.processedDepartments.length > 0) {
        const firstDept = this.processedDepartments[0];
        // 设置当前选中节点
        this.$refs.treeRef.setCurrentKey(firstDept.id);
        // 展开第一个旗县节点
        this.$refs.treeRef.store.nodesMap[firstDept.id].expanded = true;
        this.currentDepartment = this.departments.find(
          (d) => d.id === parseInt(firstDept.id.replace("dept_", ""))
        );
      }
    },
    handleClose() {
      this.clearAll();
      this.$emit("update:visible", false);
    },
    handleCancel() {
      this.clearAll();
      this.handleClose();
    },
    handleConfirm() {
      this.$emit("confirm", this.getSelectedUsers());
      this.handleClose();
    },
    // 过滤树节点
    filterNode(value, data) {
      if (!value) return true;
      return (
        data.name.includes(value) ||
        (data.isDepartment &&
          data.children.some((child) => child.name.includes(value)))
      );
    },

    // 处理树节点点击
    handleNodeClick(data) {
      if (data.isDepartment) {
        const dept = this.departments.find(
          (d) => d.id === parseInt(data.id.replace("dept_", ""))
        );
        this.currentDepartment = dept;
      } else {
        const deptId = parseInt(data.departmentId);
        const userId = parseInt(data.id.replace("user_", ""));
        const user = this.departments
          .find((d) => d.id === deptId)
          ?.users.find((u) => u.id === userId);
        if (user) {
          this.selectUser(user, deptId);
        }
      }
    },

    // 选择用户
    selectUser(user, departmentId = null) {
      const deptId = departmentId || this.currentDepartment.id;
      const deptName = this.departments.find((d) => d.id === deptId).name;

      // 检查是否已选该用户
      const existingUserIndex = this.selectedUsers.findIndex(
        (u) => u.userId === user.id
      );

      // 如果点击的是已选人员，则取消选择
      if (existingUserIndex >= 0) {
        this.selectedUsers.splice(existingUserIndex, 1);
        return;
      }

      // 如果是单选模式，清空之前的选择
      if (this.mode === "single") {
          this.selectedUsers = [{
          departmentId: deptId,
          departmentName: deptName,
          userId: user.id,
          userName: user.name,
        }];
        return
      }

      // 如果限制每个旗县只能选择一人
      if (this.limitOnePerDepartment) {
        const existingDeptIndex = this.selectedUsers.findIndex(
          (u) => u.departmentId === deptId
        );
        if (existingDeptIndex >= 0) {
          ElMessage.warning("每个旗县限选1人，请先取消当前选择");
          return;
        }
      }

      // 添加新选择
      this.selectedUsers.push({
        departmentId: deptId,
        departmentName: deptName,
        userId: user.id,
        userName: user.name,
      });
    },

    // 检查旗县是否已选
    isDepartmentSelected(departmentId) {
      return this.selectedUsers.some((u) => u.departmentId === departmentId);
    },

    // 检查用户是否已选
    isUserSelected(userId) {
      return this.selectedUsers.some((u) => u.userId === userId);
    },

    // 移除选择（通过用户ID）
    removeSelection(userId) {
      this.selectedUsers = this.selectedUsers.filter(
        (u) => u.userId !== userId
      );
    },

    // 滚动到指定旗县
    scrollToDepartment(departmentId) {
      const nodeId = `dept_${departmentId}`;
      this.$refs.treeRef.setCurrentKey(nodeId);
      this.currentDepartment = this.departments.find(
        (d) => d.id === departmentId
      );
    },

    // 清空所有选择
    clearAll() {
      this.selectedUsers = [];
    },

    // 预设已选人员
    setSelectedUsers(users = []) {
      if (!Array.isArray(users)) {
        this.selectedUsers = [];
        return;
      }
      this.selectedUsers = users
        .map((user) => {
          const departmentId = Number(user.departmentId);
          const dept =
            this.departments.find((d) => d.id === departmentId) || null;
          const candidate =
            dept?.users.find((u) => u.id === Number(user.userId)) || null;
          return {
            departmentId,
            departmentName: dept?.name || user.departmentName || "",
            userId: Number(user.userId),
            userName: candidate?.name || user.userName || "",
          };
        })
        .filter((user) => user.departmentId && user.userId);
      if (this.selectedUsers.length > 0) {
        const firstDept = this.selectedUsers[0].departmentId;
        this.$nextTick(() => {
          this.scrollToDepartment(firstDept);
        });
      }
    },

    // 获取已选人员数据
    getSelectedUsers() {
      return this.selectedUsers.map((u) => ({
        departmentId: u.departmentId,
        departmentName: u.departmentName,
        userId: u.userId,
        userName: u.userName,
      }));
    },
  },
};
</script>

<style scoped>
.department-user-selector {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.summary-bar {
  padding: 12px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.summary-bar .title {
  margin-right: 8px;
  font-weight: bold;
  line-height: 32px;
}

.summary-bar .empty-tip {
  color: #999;
  line-height: 32px;
}

.summary-bar .clear-btn {
  margin-left: auto;
}

.grouped-summary {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.county-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.county-label {
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
  min-width: 100px;
  line-height: 24px;
}

.county-users {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.user-tag {
  cursor: pointer;
}

.user-tag:hover {
  opacity: 0.8;
}

.selector-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.department-tree {
  width: 240px;
  border-right: 1px solid #eee;
  display: flex;
  flex-direction: column;
}

.department-tree .el-input {
  margin: 12px;
  width: calc(100% - 24px);
}

.department-tree .el-tree {
  flex: 1;
  overflow-y: auto;
  padding: 0 12px;
}

.tree-node {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-cards {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.department-title {
  margin: 0 0 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

.user-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #409eff;
}

.user-card {
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s;
  position: relative;
}

.user-card:hover {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.user-card.selected {
  border-color: var(--el-color-primary);
  background-color: rgba(64, 158, 255, 0.05);
}

.user-info {
  margin-top: 12px;
  text-align: center;
}

.user-info .name {
  font-weight: bold;
}

.user-info .position {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.selected-marker {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--el-color-primary);
}

.empty-tip {
  color: #999;
  text-align: center;
  line-height: 32px;
  /* padding: 12px; */
}
</style>
